import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLocationPathAndQuery } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import {
    getProductListDataViewProperty,
    isLoadMore,
} from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Pagination, { PaginationPresentationProps } from "@tCooneyBrothers/MobiusOverrides/Pagination";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";

const mapStateToProps = (state: ApplicationState) => ({
    hidePagination: isLoadMore(state),
    pagination: getProductListDataViewProperty(state, "pagination"),
    currentPathAndQuery: `${getLocation(state).pathname}${getLocation(state).search || ""}`,
});

const mapDispatchToProps = {
    addProductFilters,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductListPaginationStyles {
    container: InjectableCss;
    pagination?: PaginationPresentationProps;
}

export const paginationStyles: ProductListPaginationStyles = {
    container: {
        css: css`
            select {
                border: 1px solid #ccc !important;
                border-radius: 20px !important;
                height: 30px !important;
                padding-right: 25px !important;
                font-size: 14px !important;

                ~ [class^="IconWrapper"] {
                    top: -3px;
                    right: -14px;
                    padding: 0;
                }
            }
        `,
    },
};

const styles = paginationStyles;

const ProductListPagination = ({ addProductFilters, hidePagination, pagination, currentPathAndQuery }: Props) => {
    // const changePage = (newPageIndex: number) => {
    //     addProductFilters({ page: newPageIndex });
    //     window.scrollTo(0, 0);
    // };

    const [shouldScrollToTop, setShouldScrollToTop] = useState(false);
    useEffect(() => {
        if (shouldScrollToTop) {
            window.scrollTo(0, 0);
            setShouldScrollToTop(false);
        }
    }, [pagination?.page]);

    const changePagePLP = (newPageIndex: number, label?: string) => {
        const dataLayer = (window as any).dataLayer || [];
        if (label === "First page" || label === "Previous page") {
            if (dataLayer) {
                dataLayer.push({ ecommerce: null });
                dataLayer.push({
                    event: "previous_page_clicks",
                });
            }
        } else if (label === "Next page" || label === "Last page") {
            if (dataLayer) {
                dataLayer.push({ ecommerce: null });
                dataLayer.push({
                    event: "next_page_clicks",
                });
            }
        } else {
            // eslint-disable-next-line no-lonely-if
            if (dataLayer) {
                dataLayer.push({ ecommerce: null });
                dataLayer.push({
                    event: "plp_pagination",
                    pageIndex: newPageIndex || "",
                });
            }
        }
        addProductFilters({ page: newPageIndex });
        setShouldScrollToTop(true);
    };

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        const dataLayer = (window as any).dataLayer || [];
        if (dataLayer) {
            dataLayer.push({ ecommerce: null });
            dataLayer.push({
                event: "show_items_per_page",
                page_with_selection: newPageSize,
            });
        }
        addProductFilters({ pageSize: newPageSize });
    };

    if (hidePagination || !pagination || !pagination.totalItemCount) {
        return null;
    }

    return (
        <StyledWrapper {...styles.container}>
            <Pagination
                {...styles.pagination}
                resultsCount={pagination.totalItemCount}
                currentPage={pagination.page}
                currentPathAndQuery={currentPathAndQuery}
                resultsPerPage={pagination.pageSize}
                resultsPerPageOptions={pagination.pageSizeOptions}
                resultsPerPageLabel="show: "
                onChangePLP={changePagePLP}
                onChangeResultsPerPage={changeResultsPerPage}
                pageSizeCookie="ProductList-PageSize"
                data-test-selector="productList_pagination"
                prevPage={pagination.prevPageUri}
                nextPage={pagination.nextPageUri}
            />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListPagination),
    definition: {
        group: "Product List",
        displayName: "Pagination",
        allowedContexts: ["ProductListPage"],
    },
};

export default widgetModule;
